import LegalLayout from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="10 February 2026">
      <p>
        Anubandh AI (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) respects your privacy. This Privacy
        Policy explains what we collect, why, how we protect it, and the rights you have under
        the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and the Information
        Technology (Reasonable Security Practices) Rules, 2011.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">1. Data We Collect</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Identity data:</strong> Name, date of birth, gender, phone, email.</li>
        <li><strong>Profile &amp; biodata:</strong> Education, profession, income range, city, religion, community, mother tongue, family type &amp; values, lifestyle preferences (diet, drinking, smoking), &quot;About me&quot; and partner expectation text.</li>
        <li><strong>Sensitive data (with your explicit consent):</strong> Government ID (Aadhaar / PAN / Passport) and selfie for KYC verification, birth time &amp; place for Kundali generation.</li>
        <li><strong>Assessment data:</strong> Answers to the Marriage Intelligence Assessment (psychology, values, communication, family orientation).</li>
        <li><strong>Behavioural data:</strong> Pages visited, matches viewed, in-app chat metadata (not chat content beyond storage), device type, IP address, browser.</li>
        <li><strong>Payment data:</strong> Processed exclusively by Razorpay. We store only transaction IDs and subscription tier — <strong>never your card number, CVV, or bank credentials</strong>.</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">2. How We Use Your Data</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>To generate your Marriage Intelligence Report and compatibility scores.</li>
        <li>To surface curated matches consistent with your preferences and values.</li>
        <li>To verify your identity and prevent fraud.</li>
        <li>To process subscription payments and send transactional communications.</li>
        <li>To improve our AI models — <strong>only after anonymisation and aggregation</strong>. Your individually identifiable data is never used to train shared models.</li>
        <li>To comply with legal obligations (tax, IT Rules, law enforcement requests).</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">3. Legal Basis (DPDP Act 2023)</h2>
      <p>
        We process your personal data on the following bases: (a) your <strong>explicit consent</strong> at
        sign-up and KYC; (b) contractual necessity to deliver the subscription you purchased;
        (c) legitimate interests such as fraud prevention; and (d) compliance with Indian law.
        You may withdraw consent at any time via <a className="text-amber-700 underline" href="mailto:privacy@anubandh.ai">privacy@anubandh.ai</a>.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">4. Who We Share Data With</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Other matched users:</strong> Only your profile fields that you have chosen to make visible are shown to matches you accept.</li>
        <li><strong>Family circle:</strong> Members you invite (up to 4) can view your reports; you can revoke access anytime.</li>
        <li><strong>Service providers:</strong> Razorpay (payments), Twilio (WhatsApp alerts), Resend (email), our KYC verification partner, and our AI processing provider — each bound by data processing agreements.</li>
        <li><strong>Law enforcement:</strong> Only in response to valid legal orders under Indian law.</li>
        <li><strong>We do NOT sell your data to advertisers or data brokers. Ever.</strong></li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">5. Data Retention</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Active accounts: for the duration of your account.</li>
        <li>Deleted accounts: purged within 30 days, except transactional records retained for 8 years per the Income Tax Act.</li>
        <li>KYC documents: encrypted and stored for the minimum period required by Indian regulations.</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">6. Your Rights Under DPDP Act 2023</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Right to access:</strong> Request a copy of your data.</li>
        <li><strong>Right to correction:</strong> Update inaccurate data anytime from your Profile page.</li>
        <li><strong>Right to erasure:</strong> Delete your account and associated data.</li>
        <li><strong>Right to grievance redressal:</strong> Contact our Data Protection Officer (below).</li>
        <li><strong>Right to nominate:</strong> Nominate a person to exercise your rights in case of death or incapacity.</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">7. Security Measures</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>TLS 1.3 encryption in transit; AES-256 at rest for sensitive fields.</li>
        <li>Password hashing via bcrypt with per-user salts.</li>
        <li>Role-based access control; production data access is logged and audited.</li>
        <li>Regular security reviews and dependency vulnerability scans.</li>
      </ul>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">8. Cookies &amp; Tracking</h2>
      <p>
        We use only essential cookies for authentication and session management. We do not use
        third-party advertising cookies. Analytics, if enabled, are privacy-preserving and
        aggregated.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">9. Children</h2>
      <p>
        Anubandh AI is not intended for anyone under 18. We do not knowingly collect data from
        minors. If we discover such data, we will delete it immediately.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">10. International Users (NRI / OCI)</h2>
      <p>
        Your data is processed and stored on servers governed by Indian data protection law. By
        using the Platform from outside India, you explicitly consent to this cross-border transfer.
      </p>

      <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-8">11. Data Protection Officer &amp; Grievances</h2>
      <ul className="list-none pl-0 space-y-1">
        <li><strong>Data Protection Officer:</strong> K. Singh</li>
        <li><strong>Email:</strong> <a className="text-amber-700 underline" href="mailto:privacy@anubandh.ai">privacy@anubandh.ai</a></li>
        <li><strong>Address:</strong> Anubandh.ai, Tower A2-1206, Supertech Livingston, Crossing Republic, Near Gaur Chowk, Greater Noida, District Ghaziabad (UP) – 201016, India</li>
        <li><strong>Response window:</strong> 72 hours acknowledgement, resolution within 30 days per DPDP Act.</li>
      </ul>

      <div className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500">
        We will notify you via email and in-app of any material changes to this Privacy Policy.
      </div>
    </LegalLayout>
  );
}
